/**
 * The website's public reads in the shapes the Mini App's presentational parts
 * take (ServiceCatalogCard, HappeningSoon), so the phone pages draw the app's
 * own parts. Fields those parts never read are filled neutrally.
 */
import type { SiteEventDto, SiteEventSummaryDto, SiteHappeningSoonItem, SitePhoto, SiteServiceDto, SiteServiceSummaryDto } from '@telify/shared';

import type { CancellationInfo, FocalPoint, GalleryImage, HubEventDto, HubServiceDto } from '@/api/types';
import type { EventDetailContent } from '@/pages/SessionDetailPage/EventDetailScreen';
import type { ServiceDetailContent } from '@/pages/SessionDetailPage/ServiceDetailScreen';
import type { HappeningSoonItem } from '@/types/dashboard';

export function toHubService(s: SiteServiceSummaryDto): HubServiceDto {
  const amount = s.price.kind === 'FREE' ? null : s.price.amount;
  return {
    id: s.id,
    name: s.name,
    description: null,
    duration: s.durationMinutes,
    price: amount,
    paymentRequirement: s.paymentRequirement,
    depositAmount: null,
    depositType: null,
    imageUrl: s.photo?.url ?? null,
    focalPointX: s.photo?.focalX ?? null,
    focalPointY: s.photo?.focalY ?? null,
    focalPointScale: s.photo?.focalScale ?? null,
    practitionerCount: 1,
    sortOrder: 0,
    summary: s.summary,
    modality: s.modality,
    pricing: { kind: s.price.kind, amount },
    availabilityState: 'AVAILABLE',
  };
}

/** The service and event pages' parts (DetailHero, ServiceStats, DetailGallery, DetailInfoRow, DetailStickyBar). */
export function toDetailParts(s: SiteServiceDto | SiteEventDto) {
  const focalPoint: FocalPoint = { x: s.photo?.focalX ?? null, y: s.photo?.focalY ?? null, scale: s.photo?.focalScale ?? null };
  const gallery: GalleryImage[] = s.gallery.map((p: SitePhoto) => ({ url: p.url, focalX: p.focalX, focalY: p.focalY }));
  // The website sends the terms as the app words them; the policy kind is not shown.
  const cancellation: CancellationInfo = { policy: 'CUSTOM', cutoffMinutes: null, displayText: s.cancellationText };
  const amount = s.price.kind === 'FREE' ? null : s.price.amount;
  return { focalPoint, gallery, cancellation, pricing: { kind: s.price.kind, amount } };
}

/** The shared service page's service (ServiceDetailScreen). */
export function toServiceDetail(s: SiteServiceDto): ServiceDetailContent {
  const { focalPoint, gallery, cancellation, pricing } = toDetailParts(s);
  return {
    name: s.name,
    summary: s.summary,
    description: s.description,
    duration: s.durationMinutes,
    modality: s.modality,
    pricing,
    imageUrl: s.photo?.url ?? null,
    focalPoint,
    gallery,
    location: s.location,
    cancellation,
    acceptingBookings: s.acceptingBookings,
    payment: s.payment,
  };
}

/** The shared event page's event (EventDetailScreen). */
export function toEventDetail(e: SiteEventDto): EventDetailContent {
  const { focalPoint, gallery, cancellation, pricing } = toDetailParts(e);
  return {
    title: e.title,
    summary: e.summary,
    description: e.description,
    imageUrl: e.photo?.url ?? null,
    focalPoint,
    gallery,
    startTime: e.startTime,
    endTime: e.endTime,
    modality: e.modality,
    spotsRemaining: e.spotsLeft,
    capacity: e.capacity,
    isBookable: e.isBookable,
    cancellation,
    price: pricing.amount,
    payment: e.payment,
    location: e.location,
  };
}

/** The Book screen's event card (EventCatalogCard), with the signed-in viewer's own booking on it (row B4). */
export function toHubEvent(e: SiteEventSummaryDto): HubEventDto {
  const amount = e.price.kind === 'FREE' ? null : e.price.amount;
  const minutes = Math.round((new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60_000);
  return {
    id: e.id,
    title: e.title,
    description: null,
    imageUrl: e.photo?.url ?? null,
    focalPointX: e.photo?.focalX ?? null,
    focalPointY: e.photo?.focalY ?? null,
    focalPointScale: e.photo?.focalScale ?? null,
    startTime: e.startTime,
    endTime: e.endTime,
    timezone: '',
    practitioner: { id: '', userId: '', name: '', photoUrl: null },
    service: { id: '', name: e.title, duration: minutes, price: amount },
    isBookable: e.isBookable,
    registrationClosed: e.registrationClosed,
    spotsRemaining: e.spotsLeft,
    capacity: e.capacity,
    locationName: e.location.isVirtual ? null : e.location.name,
    locationCity: e.location.city,
    locationState: e.location.state,
    isVirtual: e.location.isVirtual,
    virtualPlatform: e.location.virtualPlatform,
    userBooking: e.userBooking,
    summary: e.summary,
    modality: e.modality,
  };
}

/** The guest home's Happening Soon card (HappeningSoon): an event or a series, as the app's Home lists them (row H7). */
export function toHappeningItem(item: SiteHappeningSoonItem): HappeningSoonItem {
  if (item.kind === 'event') {
    const { path: _path, ...event } = item;
    return event;
  }
  const { path: _path, ...series } = item;
  // The two ids the card never reads; the website reads no person's id.
  return { ...series, practitionerId: '', serviceId: '' };
}
